/**
 * Package Status Download (#245) — pure helpers for the dialog.
 * Dates are 'YYYY-MM-DD' strings; day math goes through utils/calendarDate.
 */
import { addCalendarDays, toCalendarDate } from '../../../utils/calendarDate';
import { formatDate } from '../../../utils/formatDate';

export const DATE_MODES = [
  { value: 'current', label: 'Current' },
  { value: 'through', label: 'Through date' },
  { value: 'between', label: 'Between dates' },
];

export const FORMATS = [
  { value: 'xlsx', label: 'Excel (.xlsx)' },
  { value: 'csv', label: 'CSV' },
];

// Groups open when the column picker first expands (plan §4.5).
export const DEFAULT_EXPANDED_GROUPS = ['identity', 'hours'];

export const NOT_FOR_BETWEEN = 'Not for between dates';

/** true when the calendar date is a Sunday. */
export const isSunday = (ymd) => toCalendarDate(ymd)?.getDay() === 0;

/**
 * Most recent Sunday strictly before today. Fri 2026-10-02 → 2026-09-27;
 * Sun 2026-09-27 → 2026-09-20. Same rule as the backend.
 */
export const lastWeekEnding = (todayYmd) => {
  const day = toCalendarDate(todayYmd).getDay(); // 0 = Sunday
  return addCalendarDays(todayYmd, -(day === 0 ? 7 : day));
};

/** Settings used when nothing is saved for the project. */
export const defaultSettings = (catalog) => ({
  mode: 'through',
  throughDate: null, // null = "Last week ending"
  startDate: null,
  endDate: null,
  applyBoardFilters: false,
  includeGeneralConditions: false,
  includeChangeOrders: false,
  columns: catalog ? catalog.columns.filter((c) => c.default).map((c) => c.key) : null,
  format: 'xlsx',
  columnsExpanded: false,
});

/**
 * Saved settings merged over the defaults, with unknown or duplicate column
 * keys dropped (the catalog may have changed since they were saved).
 * Without a catalog, columns are kept as saved and cleaned once it loads.
 */
export const sanitizeSettings = (saved, catalog) => {
  const defaults = defaultSettings(catalog);
  const merged = { ...defaults, ...(saved && typeof saved === 'object' ? saved : {}) };
  if (!DATE_MODES.some((m) => m.value === merged.mode)) merged.mode = defaults.mode;
  if (!FORMATS.some((f) => f.value === merged.format)) merged.format = defaults.format;
  if (catalog) {
    const known = new Set(catalog.columns.map((c) => c.key));
    const columns = Array.isArray(merged.columns)
      ? [...new Set(merged.columns.filter((k) => known.has(k)))]
      : [];
    merged.columns = columns.length > 0 ? columns : defaults.columns;
  }
  return merged;
};

/** Through date in effect: the chosen Sunday, or last week ending. */
export const effectiveThroughDate = (settings, todayYmd) =>
  settings.throughDate || lastWeekEnding(todayYmd);

/** Default Between range: last week, Monday through Sunday. */
export const defaultBetweenRange = (todayYmd) => {
  const end = lastWeekEnding(todayYmd);
  return { startDate: addCalendarDays(end, -6), endDate: end };
};

/**
 * Date errors for the current mode → { through?, start?, end? } (one sentence each).
 */
export const dateErrors = (settings, todayYmd) => {
  const errors = {};
  if (settings.mode === 'through') {
    const through = effectiveThroughDate(settings, todayYmd);
    if (!toCalendarDate(through)) errors.through = 'Enter a valid date.';
    else if (!isSunday(through)) errors.through = 'Pick a Sunday.';
  }
  if (settings.mode === 'between') {
    if (!toCalendarDate(settings.startDate)) errors.start = 'Enter a valid date.';
    if (!toCalendarDate(settings.endDate)) errors.end = 'Enter a valid date.';
    if (!errors.start && !errors.end && settings.endDate < settings.startDate) {
      errors.end = 'End date must be on or after the start date.';
    }
  }
  return errors;
};

/** The note under the dates (plan §1.3). */
export const dateNote = (settings, todayYmd) => {
  if (settings.mode === 'current') {
    return 'Actual hours and planned hours to date run through today. Percent complete, earned hours, dates and status are the current values.';
  }
  if (settings.mode === 'between') {
    return 'Shows actual and planned hours for this period only. Percent complete and dates are the current values. Earned, burned %, forecast and left to complete are not available for a between-dates range.';
  }
  const through = effectiveThroughDate(settings, todayYmd);
  return `Actual hours and planned hours to date run through Sunday ${formatDate(through)}. Percent complete, earned hours, dates and status are the current values.`;
};

/** File-name suffix, same as the backend's: Current_…, Thru_…, start_to_end. */
export const fileNameSuffix = (settings, todayYmd) => {
  if (settings.mode === 'current') return `Current_${todayYmd}`;
  if (settings.mode === 'between') return `${settings.startDate}_to_${settings.endDate}`;
  return `Thru_${effectiveThroughDate(settings, todayYmd)}`;
};

const sanitizeFileNamePart = (value) =>
  value === null || value === undefined
    ? ''
    : String(value)
        // eslint-disable-next-line no-control-regex
        .replace(/[\s<>:"/\\|?*\x00-\x1F]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');

/**
 * Predicted download name for the footer. Mirrors the backend's buildFileName
 * (services/packageExport/packageExportCalculations.js); the real name comes
 * from the response.
 */
export const buildFileName = ({ projectNumber, projectName, suffix, format }) => {
  const name = sanitizeFileNamePart(projectName).slice(0, 50).replace(/_+$/, '');
  const base = [sanitizeFileNamePart(projectNumber), name, 'Package_Status', suffix]
    .filter(Boolean)
    .join('_');
  return `${base}.${format === 'csv' ? 'csv' : 'xlsx'}`;
};

/** Catalog → TransferList groups, with Between-dates labels and disabled items. */
export const toTransferGroups = (catalog, isPeriod) =>
  catalog.groups.map((group) => ({
    id: group.id,
    label: group.label,
    note: group.note,
    items: catalog.columns
      .filter((c) => c.group === group.id)
      .map((c) => ({
        id: c.key,
        label: isPeriod && c.periodLabel ? c.periodLabel : c.label,
        disabled: isPeriod && !!c.cumulative,
        disabledReason: NOT_FOR_BETWEEN,
      })),
  }));

/** Selected items left out of a Between-dates download (cumulative ones). */
export const leftOutColumns = (catalog, columns, isPeriod) => {
  if (!isPeriod || !catalog) return [];
  const cumulative = new Set(catalog.columns.filter((c) => c.cumulative).map((c) => c.key));
  return columns.filter((k) => cumulative.has(k));
};

/** Active board filters as pill labels, e.g. ["PM: Pat Lee", "Prefab required"]. */
export const boardFilterLabels = (boardFilters) => {
  if (!boardFilters) return [];
  const labels = [];
  if (boardFilters.pm) labels.push(`PM: ${boardFilters.pm.name}`);
  if (boardFilters.resource) labels.push(`Resource: ${boardFilters.resource.name}`);
  if (boardFilters.vdcRequired) labels.push('VDC required');
  if (boardFilters.prefabRequired) labels.push('Prefab required');
  return labels;
};

/** "{n} columns selected" (+ " · {m} left out"). */
export const columnsSummary = (count, leftOut) =>
  `${count} ${count === 1 ? 'column' : 'columns'} selected${leftOut > 0 ? ` · ${leftOut} left out` : ''}`;

/** Browser time zone for the backend's "today" and timestamps; null if unknown. */
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/**
 * POST body for /api/package-export/:projectId/export (plan §3.4).
 * Left-out (cumulative) columns are not sent in Between mode.
 */
export const buildExportRequest = ({ settings, catalog, workflowId, boardFilters, todayYmd, timeZone }) => {
  const isPeriod = settings.mode === 'between';
  const leftOut = new Set(leftOutColumns(catalog, settings.columns, isPeriod));
  let dateRange;
  if (settings.mode === 'current') dateRange = { mode: 'current' };
  else if (isPeriod) dateRange = { mode: 'between', startDate: settings.startDate, endDate: settings.endDate };
  else dateRange = { mode: 'through', throughDate: effectiveThroughDate(settings, todayYmd) };

  return {
    statusStepSetId: workflowId,
    dateRange,
    filters: {
      applyBoardFilters: settings.applyBoardFilters,
      includeGeneralConditions: settings.includeGeneralConditions,
      includeChangeOrders: settings.includeChangeOrders,
      board: settings.applyBoardFilters
        ? {
            pmEmployeeId: boardFilters?.pm?.id ?? null,
            resourceEmployeeId: boardFilters?.resource?.id ?? null,
            vdcRequired: !!boardFilters?.vdcRequired,
            prefabRequired: !!boardFilters?.prefabRequired,
          }
        : null,
    },
    columns: settings.columns.filter((k) => !leftOut.has(k)),
    format: settings.format,
    ...(timeZone ? { timeZone } : {}),
  };
};

/**
 * What is saved as last-used settings: everything except board filter values
 * (never saved). A through date equal to last week ending is saved as null so
 * reopening next week picks the new last week ending.
 */
export const settingsToSave = (settings, todayYmd) => ({
  ...settings,
  throughDate:
    settings.throughDate && settings.throughDate === lastWeekEnding(todayYmd)
      ? null
      : settings.throughDate,
});
